import React, { useCallback, useEffect } from 'react';
import { isEqual } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';

// MUI components
import { Grid, Card, CardContent, Hidden } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { LinearScale } from '@material-ui/icons';

// components
import Header from 'components/Header';
import Table from 'components/Table';
import ExportButton from 'components/ExportButton';
import ReportRefreshButton from 'components/ReportRefreshButton';

// action creators
import { createMilestoneUsageReport } from 'store/actions/apiTaskActions';

// selectors
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getForm from 'store/selectors/getForm';
import getSliceEntityById from 'store/selectors/getSliceEntityById';

const REPORT_FORM = 'milestoneUsageReportForm';
const rowsPerPage = 10;

export default function MilestoneUsageCard() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const initialOrder = 'asc';
  const initialOrderBy = 'milestoneTitle';
  const {
    currentGroupId,
    report: {
      data: {
        content: unformattedContent = [],
        meta: { columns: unformattedColumns = [] } = {},
      } = {},
      id: reportId,
    },
    form: { isSubmitting },
  } = useSelector(
    state => ({
      currentGroupId: getCurrentGroupId(state),
      form: getForm(REPORT_FORM)(state),
      report: getSliceEntityById(
        'report',
        'ReportTypes.MILESTONE_USAGE',
        {}
      )(state),
    }),
    isEqual
  );
  let columns = unformattedColumns;
  const content = unformattedContent.map(
    ({ leadsTagged = [], ...rest }, index: any) => {
      let leadsTaggedString = leadsTagged.slice(0, 3).join(', ');
      let othersTagged;
      if (leadsTagged?.length > 3) {
        leadsTaggedString = `${leadsTaggedString}, +${leadsTagged.length -
          3} others`;
        // Assigns overflow names to tooltip
        othersTagged = `+ ${leadsTagged.slice(3).join(', ')}`;
      }
      return {
        ...rest,
        tooltipTitle: othersTagged,
        leadsTagged: leadsTaggedString,
      };
    }
  );

  const dispatchCreateReport = useCallback(
    ({ forceRefresh }: { forceRefresh: boolean }) => {
      dispatch(
        createMilestoneUsageReport({ groupId: currentGroupId, forceRefresh })
      );
    },
    [dispatch, currentGroupId]
  );

  useEffect(() => {
    dispatchCreateReport({ forceRefresh: false });
  }, [dispatchCreateReport]);

  return (
    <Card>
      <CardContent className={classes.cardContent}>
        <Grid container>
          <Grid item xs>
            <Header title='Milestone Usage' icon={LinearScale} compact />
          </Grid>

          <Hidden xsDown>
            <Grid item xs={2}>
              <Grid container justifyContent='flex-end'>
                <Grid item>
                  <ReportRefreshButton
                    loading={isSubmitting}
                    onRefresh={dispatchCreateReport}
                  />
                </Grid>
                <ExportButton params={{ reportId }} disabled={isSubmitting} />
              </Grid>
            </Grid>
          </Hidden>

          <Grid item xs={12}>
            <Table
              columns={columns}
              data={content}
              variant='outlined'
              numberOfSkeletonRows={isSubmitting ? 8 : 0}
              initialOrder={initialOrder}
              initialOrderBy={initialOrderBy}
              TableRowProps={{
                classes: {
                  root: (classes as any).root,
                },
              }}
              TableCellProps={{
                className: (classes as any).tableCell,
              }}
              HeaderTableCellProps={{
                className: (classes as any).headerTableCell,
              }}
              TablePaginationProps={{
                count: content.length,
                rowsPerPage,
                rowsPerPageOptions: [rowsPerPage],
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
const useStyles = makeStyles(theme => ({
  cardContent: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  exportButton: {
    textAlign: 'right',
  },
}));
